import { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { api, formatError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { renderTemplateBody } from "@/components/PDFPreviews";
import DocumentSheet from "@/components/DocumentSheet";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Upload, Download, Info, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteConfig } from "@/lib/siteConfig";

/**
 * Advisory notes attached to particular answers — shown, never enforced.
 *
 * **Warns, does not block.** The customer may have good reasons for what they entered and it is
 * their document; refusing to draft it would be this platform overruling them. But saying nothing
 * is worse: the person most likely to enter a 24-month tenancy is the person who does not know why
 * almost every Indian rental agreement is written for eleven.
 *
 * The registration note exists because of a real, delayed harm. A lease of twelve months or more
 * must be registered at the Sub-Registrar; an unregistered one generally cannot be used to prove
 * the tenancy's terms. Nothing goes wrong at signing — it goes wrong eighteen months later in a
 * dispute, when the clauses the landlord is relying on turn out to be the ones they cannot enforce.
 * By then the document has been paid for, printed and signed, and this platform never mentioned
 * that a step was missing.
 *
 * Deliberately advisory in tone and not framed as legal advice: it states the requirement and that
 * we do not perform registration. See /disclaimer.
 */
function FieldNotice({ fieldKey, value }) {
  if (fieldKey !== "lease_duration_months") return null;
  const months = Number(value);
  if (!Number.isFinite(months) || months < 12) return null;
  return (
    <div data-testid="notice-lease-registration"
         className="mt-2 flex gap-2 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/[0.06] px-3 py-2">
      <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B] mt-0.5 shrink-0" strokeWidth={1.5} />
      <div className="text-[11px] text-[#D4B483] leading-relaxed">
        A tenancy of <b>12 months or more must be registered</b> at the Sub-Registrar's office.
        An unregistered lease of this length generally cannot be used to prove its terms in a
        dispute. DharmaTN prepares and delivers the agreement on stamp paper but{" "}
        <b>does not carry out registration</b> — you will need to complete that separately.
        Many agreements are written for 11 months for exactly this reason.
      </div>
    </div>
  );
}

// Labels, types and options all come from the template's field definitions now — they used to be
// two hardcoded maps in this file, so adding a field meant editing the wizard.

// Chunk fields into steps of n — the fallback used for the first paint, before the form column
// has been measured.
const chunk = (arr, n) => arr.reduce((a, _, i) => (i % n ? a : [...a, arr.slice(i, i + n)]), []);

// ---------------------------------------------------------------------------------------------
// Step packing
//
// **A step holds what fits, and no more.** It used to be a flat four fields per step, which has
// no relationship to how tall those four fields are: four short inputs left half the column
// empty, while four long-text fields (which render as textareas) overflowed it and put the form
// on a scrollbar. Scrolling a form is the thing paging through it is meant to avoid — so the
// fields are packed against the measured height of the column instead, and anything that doesn't
// fit starts another step.
//
// Heights are computed from the markup below rather than measured per field: the shapes are
// fixed and few, and measuring would mean rendering a step to find out it was the wrong step.
// They are deliberate over-estimates by a pixel or two — a step that ends slightly early is
// invisible, a step that ends slightly late brings back the scrollbar.
// Tightened from the original space-y-5/mt-2/mb-6 pass: the packing made steps correct, not
// dense, and four short fields with 20px between each and 24px around every section read as far
// more empty column than four answers need. Every constant below still over-estimates by a pixel
// or two on purpose (see the comment above `packSteps`) — only the *targets* got smaller, not the
// safety margin.
const FIELD_GAP = 12;      // space-y-3 between fields
const LABEL_H = 16;        // the uppercase field label
const INPUT_H = 42;        // mt-1 + h-9 control
const TEXTAREA_H = 80;     // mt-1 + min-h-[60px]
const COUNTER_H = 18;      // the "12/60" line under a length-capped text field
const PROGRESS_H = 42;     // step counter row + progress bar + margins
const NAME_BLOCK_H = 114;  // "Name this document", on the first step only
const LAST_NOTE_H = 68;    // the delivery note, on the last step only
const HINT_H = 84;         // the landscape-preview hint, below `md` only

// A long text field renders as a textarea; everything else is a single-line control.
const isTextarea = (f) => f.type === "text" && (f.max_length == null || f.max_length > 120);
const fieldHeight = (f) =>
  LABEL_H + (isTextarea(f) ? TEXTAREA_H : INPUT_H) +
  (f.max_length && f.type === "text" ? COUNTER_H : 0) + FIELD_GAP;

/**
 * Greedily pack `fields` into steps that fit `availH`, honouring the extras each step carries.
 *
 * The first step also shows the document-name block and (below `md`) the preview hint; the last
 * shows the delivery note. Which step is last isn't known until packing finishes, so the note is
 * checked afterwards and the final step split again if it no longer fits.
 */
function packSteps(fields, availH, isDesktop) {
  if (!fields.length) return [];
  const extras = (i) =>
    PROGRESS_H + (i === 0 ? NAME_BLOCK_H + (isDesktop ? 0 : HINT_H) : 0);
  const out = [];
  let cur = [], used = 0;
  for (const f of fields) {
    const h = fieldHeight(f);
    const budget = availH - extras(out.length);
    // `cur.length &&` so a field taller than the whole column still lands somewhere rather than
    // looping forever — that step scrolls, which is the honest outcome on a very short window.
    if (cur.length && used + h > budget) { out.push(cur); cur = []; used = 0; }
    cur.push(f); used += h;
  }
  if (cur.length) out.push(cur);
  // The delivery note lands on whichever step ended up last; if it no longer fits, move that
  // step's final field onto a step of its own.
  const lastIdx = out.length - 1;
  const lastUsed = out[lastIdx].reduce((n, f) => n + fieldHeight(f), 0);
  if (out[lastIdx].length > 1 && lastUsed + LAST_NOTE_H > availH - extras(lastIdx)) {
    out.push([out[lastIdx].pop()]);
  }
  return out;
}

// Mirrors DOCUMENT_NAME_MAX in backend/core/templates_data.py, which truncates anything longer.
const NAME_MAX = 20;

// Mirrors _DOC_NAME_PREFIX in backend/core/templates_data.py — only so the wizard can *show* the
// customer the name their document will get if they leave the box empty. The server is what
// actually assigns it (auto_document_name); this never travels anywhere.
const TYPE_PREFIX = {
  rental_agreement: "Rental",
  affidavit: "Affidavit",
  contract_review: "Contract",
  property_verification: "Property",
};
const shortType = (serviceType) =>
  TYPE_PREFIX[serviceType] || String(serviceType || "Document").split("_")[0].replace(/^./, c => c.toUpperCase());

export default function Wizard() {
  const { serviceType } = useParams();
  const [sp] = useSearchParams();
  const affType = sp.get("type");
  const nav = useNavigate();

  // Contract review / property verification = upload flow (no template)
  if (serviceType === "contract_review" || serviceType === "property_verification") {
    return <UploadFlow serviceType={serviceType} />;
  }

  // `?type=` names the template within a group; without it the route itself is the template.
  const templateKey = affType || serviceType;
  const editTxnId = sp.get("txn");
  return <FormFlow templateKey={templateKey} fallbackService={serviceType} editTxnId={editTxnId} />;
}

function FormFlow({ templateKey, fallbackService, editTxnId }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const isLab = !!useSiteConfig().is_lab;
  // Their own name in the example, so the suggestion reads like something they'd actually type.
  const firstName = (user?.name || "Rajam").split(" ")[0];
  const [tpl, setTpl] = useState(null);
  const [data, setData] = useState({});
  // The customer's own label for this document. Optional — blank falls back to the document's
  // type everywhere it's shown, which is what every document created before this looked like.
  const [docName, setDocName] = useState("");
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [txnId, setTxnId] = useState(null);
  // nothing to load for a new document, so it starts ready
  const [loaded, setLoaded] = useState(!editTxnId);

  const [priceCfg, setPriceCfg] = useState(null);
  // A 404 here means the document is no longer on sale (admin disabled it) — say so rather than
  // leaving the wizard on "Loading form…" forever.
  const [unavailable, setUnavailable] = useState(false);
  useMemo(() => { api.get(`/templates/${templateKey}`).then(r => setTpl(r.data)).catch(() => setUnavailable(true)); }, [templateKey]);
  useMemo(() => { api.get("/pricing").then(r => setPriceCfg(r.data)).catch(() => {}); }, []);
  // The template's group is the transaction's service type — so a template created under any
  // group works without a code change here. Pricing resolves per template, falling back to the
  // group rate, mirroring core.templates_data.compute_line.
  const service = tpl?.group || fallbackService;
  const pricing = priceCfg ? (priceCfg[templateKey] || priceCfg[service]) : null;
  // A date field opens on today rather than empty. Gated on `loaded` because the draft loader
  // below *replaces* `data` wholesale — filling defaults before it resolves would be silently
  // undone. For a new document there is nothing to wait for.
  useEffect(() => {
    if (!tpl || !loaded) return;
    const today = new Date().toISOString().slice(0, 10);
    setData(d => {
      const next = { ...d };
      let changed = false;
      for (const f of tpl.fields || []) {
        if (f.type === "date" && !next[f.key]) { next[f.key] = today; changed = true; }
      }
      return changed ? next : d;
    });
  }, [tpl, loaded]);

  useEffect(() => {
    if (!editTxnId) return;
    // `alive` guards against a stale resolution clobbering newer state — the concrete case is
    // React 18 Strict Mode's dev-only double-invoke (mount, cleanup, mount again), which fires this
    // effect twice with no cancellation; without the guard, the first invocation's fetch can still
    // resolve *after* the second's, silently reverting `data`/`docName` back to what was loaded
    // first. Also correct if `editTxnId` itself ever changes mid-flight.
    let alive = true;
    api.get(`/transactions/${editTxnId}`).then(r => {
      if (!alive) return;
      setData(r.data.form_data || {});
      setDocName(r.data.document_name || "");
      setTxnId(r.data.id);
    }).catch(() => { if (alive) toast.error("Could not load saved draft"); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [editTxnId]);
  // How much room the fields have. Measured from the column itself, which is sized by the flex
  // shell and *not* by its content — so this can't feed back into the packing it drives.
  const fieldsBoxRef = useRef(null);
  const [availH, setAvailH] = useState(0);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true);
  useLayoutEffect(() => {
    const el = fieldsBoxRef.current;
    if (!el) return;
    const measure = () => {
      // clientHeight includes the column's own padding, and that padding is responsive
      // (p-6 / md:p-8) — so read it rather than assume it, and pack against what's left.
      const cs = getComputedStyle(el);
      const pad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      setAvailH(Math.max(0, (el.clientHeight || 0) - pad));
      setIsDesktop(window.matchMedia("(min-width: 768px)").matches);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tpl]);

  const steps = useMemo(() => {
    const fields = tpl?.fields || [];
    // Four-per-step until the column has been measured — one paint at the old shape beats a
    // flash of every field on one step.
    if (!fields.length || !availH) return chunk(fields, 4);
    return packSteps(fields, availH, isDesktop);
  }, [tpl, availH, isDesktop]);

  // Repacking can leave fewer steps than before (rotating a phone, resizing a window), and the
  // customer may be standing on one that no longer exists.
  useEffect(() => {
    setStep(s => Math.min(s, Math.max(0, steps.length - 1)));
  }, [steps.length]);

  const progress = steps.length ? Math.round(((step + 1) / steps.length) * 100) : 0;
  const allFilled = useMemo(() => !!tpl && (tpl.fields || []).every(f => String(data[f.key] ?? "").trim() !== ""), [tpl, data]);

  const upd = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const setField = (k, v) => setData(d => ({ ...d, [k]: v }));

  // The three writes below all carry the same payload, so the name can never be saved by one
  // path and dropped by another.
  const createBody = () => ({
    service_type: service, subtype: templateKey,
    document_name: docName, form_data: data, price: pricing?.total || 999,
  });
  const updateBody = () => ({ document_name: docName, form_data: data });

  const saveDraft = async () => {
    setBusy(true);
    try {
      if (txnId) { await api.put(`/transactions/${txnId}`, updateBody()); toast.success("Draft saved"); }
      else { const { data: t } = await api.post("/transactions", createBody()); setTxnId(t.id); toast.success("Saved as draft"); }
    // The server's reason, not "Save failed" — the draft ceiling
    // (require_draft_headroom) reports here, and "Save failed" would leave the customer with no
    // idea that deleting an old draft is what unblocks them.
    } catch (e) { toast.error(formatError(e?.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  // Lab-only: re-typing every field by hand on every test pass is the actual time cost of testing
  // this wizard, not the thing being tested. Export dumps the current answers so a tester can save
  // a "usual" filled-in set once; Import replays it. Gated on `isLab` further down — neither
  // button exists in a production build.
  const exportTestData = () => {
    const blob = new Blob([JSON.stringify({ document_name: docName, form_data: data }, null, 2)],
                          { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${templateKey}-test-data.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const importTestData = (e) => {
    const f = e.target.files[0];
    e.target.value = ""; // so picking the same file twice in a row still fires onChange
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        // Accepts either this export's own shape ({document_name, form_data}) or a bare
        // {field: value} object, so a hand-written fixture works just as well as an exported one.
        setData(parsed.form_data && typeof parsed.form_data === "object" ? parsed.form_data : parsed);
        if (parsed.document_name) setDocName(parsed.document_name);
        toast.success("Test data imported");
      } catch { toast.error("That file isn't valid JSON"); }
    };
    reader.readAsText(f);
  };

  const addToCart = async () => {
    setBusy(true);
    try {
      let id = txnId;
      if (!id) { const { data: t } = await api.post("/transactions", createBody()); id = t.id; }
      else { await api.put(`/transactions/${id}`, updateBody()); }
      await api.post(`/cart/items/${id}`);
      toast.success("Added to cart");
      nav("/customer/cart");
    } catch (e) { toast.error(formatError(e?.response?.data?.detail) || "Failed to add to cart"); }
    finally { setBusy(false); }
  };

  if (unavailable) return (
    <div className="p-10 bg-[#080C16] min-h-screen text-[#A0AABF]">
      <div className="max-w-md">
        <h1 className="font-serif-display text-2xl text-white mb-2">This document isn't available</h1>
        <p className="text-sm">It may have been withdrawn. Please pick another from your dashboard.</p>
        <Link to="/customer/new" className="inline-block mt-4 text-[#CBA96C] hover:underline">Choose a document →</Link>
      </div>
    </div>
  );
  if (!tpl) return <div className="p-10 text-[#A0AABF] bg-[#080C16] min-h-screen">Loading form…</div>;
  // The template is the single source of truth for what the document says. Rendering it here is
  // what makes the wizard preview, the admin document editor, the customer's "View document" and
  // the delivered PDF all show the same text. A template with no body shows an empty sheet —
  // visible, and fixable by admin in the Template Catalog without a code change.
  const templateBody = (tpl.body_markdown || "").trim();
  // `fit="container"` so the sheet sizes to the preview pane rather than to its width alone, and
  // the title rides in the sheet's own control row instead of costing a heading above it.
  const Preview = (
    <DocumentSheet title="Live Preview" fit="container" body={renderTemplateBody(templateBody, data)} />
  );
  const isLast = step >= steps.length - 1;

  return (
    // An app shell, not a document: **exactly** one viewport tall, and it does not scroll.
    //
    // It was `min-h-screen` with the form column growing to fit its content, so a step with four
    // fields pushed Back/Next below the fold and the whole page scrolled to reach them — while
    // the preview beside it stayed put. Fixing the height and giving the two columns their own
    // scrolling is what keeps Save Draft, the fields, Back/Next and the preview all on screen at
    // once. Renders inside CustomerLayout full-bleed; the sidebar is always present.
    <div className="h-[calc(100vh-3.5rem)] md:h-screen overflow-hidden bg-[#080C16] text-[#F9F8F6] flex flex-col">
      <div className="shrink-0 backdrop-blur-xl bg-[#080C16]/80 border-b border-white/5">
        <div className="px-6 h-14 flex items-center justify-between text-sm gap-3">
          <Link to="/customer" data-testid="wizard-back-link" className="inline-flex items-center gap-1 text-sm text-[#A0AABF] hover:text-white transition-colors"><ChevronLeft className="w-4 h-4" strokeWidth={1.5}/> Back</Link>
          <div className="font-serif-display text-white">{tpl.title}</div>
          <div className="flex items-center gap-2">
            {/* Test-data export/import — lab only (see backend/CLAUDE.md "ENV"). Filling every
                field by hand on every test pass is the actual cost of testing this form; this
                lets a tester save a filled-in set once and replay it, instead of re-typing it
                every time. Never rendered outside a lab/dev build. */}
            {/* Gated on `loaded`, not just `isLab`: when resuming an existing draft (`?txn=`),
                the template and the draft's own form_data arrive over two separate, unordered
                fetches (see the effects above). The button becomes clickable the instant the
                template resolves — if the draft fetch is still in flight, importing here would
                succeed only for it to be silently overwritten moments later when that slower
                fetch's `setData` finally lands. Hiding the controls until `loaded` is true (which
                for a brand-new document is always already the case) removes the window entirely. */}
            {isLab && loaded && (
              <>
                <Button variant="outline" size="sm" onClick={exportTestData} data-testid="wizard-export-json"
                  title="Export the current answers as JSON (test data only)"
                  className="bg-transparent border-white/20 text-white hover:bg-white/5 h-8 text-xs">
                  <Download className="w-3.5 h-3.5"/>
                </Button>
                <label className="cursor-pointer" title="Import answers from a JSON file (test data only)">
                  <input type="file" accept=".json,application/json" className="hidden" onChange={importTestData} data-testid="wizard-import-json"/>
                  <Button variant="outline" size="sm" asChild
                    className="bg-transparent border-white/20 text-white hover:bg-white/5 h-8 text-xs">
                    <span><Upload className="w-3.5 h-3.5"/></span>
                  </Button>
                </label>
              </>
            )}
            {/* The step counter lives with the progress bar now — it describes the form, not the
                page, and reading "Step 2 of 5" in the top bar meant looking away from the thing
                it was counting. */}
            <Button variant="outline" size="sm" disabled={busy} onClick={saveDraft} data-testid="wizard-save-draft"
              className="bg-transparent border-white/20 text-white hover:bg-white/5 h-8 text-xs">{busy?"…":"Save Draft"}</Button>
          </div>
        </div>
      </div>

      {/* Persistent, not dismissible — same idea as the admin maintenance banner (AdminLayout.jsx):
          a business-relevant notice that should stay in view for as long as it's relevant, not one
          click away from being forgotten. Blue, matching the app's existing "informational, not a
          warning" family (see the landscape-preview hint below) rather than the admin banner's
          orange, which reads as "something is wrong" — nothing is wrong here, this is a standing
          option. `shrink-0` keeps the fixed-height wizard shell's budget intact (see the shell
          comment above `h-[calc(100vh-3.5rem)]`) — it takes its row like the header does, rather
          than eating into the space the form/preview columns need. */}
      <div data-testid="wizard-custom-format-notice"
           className="shrink-0 flex items-start gap-2.5 border-b border-[#7DB2FB]/20 bg-[#7DB2FB]/10 px-6 py-2.5 text-xs text-[#7DB2FB]">
        <Info className="w-4 h-4 mt-px shrink-0" strokeWidth={1.5}/>
        <span>
          <strong className="font-semibold">Need a different format?</strong> You're not limited to this
          template — complete the payment for this document, then contact us and attach your own file.
          We'll prepare it in your format instead.
        </span>
      </div>

      <div className="flex-1 min-h-0 grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        {/* LEFT: FORM — fields scroll, the action bar underneath does not. */}
        <div className="flex flex-col min-h-0 border-r border-white/5 bg-[#111623]">
          {/* `overflow-y-auto` is a safety net, not the plan: packSteps sizes each step to this
              box, so it should never engage. It stays because a very short window can leave a
              single textarea taller than the whole column, and an unreachable field is worse
              than a scrollbar. */}
          <div ref={fieldsBoxRef} className="flex-1 min-h-0 overflow-y-auto p-6 md:p-8">
          <div className="max-w-md">
            {/* The live preview pane is `hidden md:block` — an upright phone has nowhere to put a
                second column, and shrinking an A4 sheet into half a 390px screen would be
                unreadable anyway. Rather than let the preview silently not exist, say where it
                went: turning the phone crosses the 768px breakpoint and brings it back. */}
            {/* Blue, and the same weight as the red draft-limit banner on the dashboard: these are
                the two things the app tells a customer unprompted, so they should read as one
                family — red when something is refused, blue when something is merely available. */}
            <div data-testid="wizard-preview-hint" className="md:hidden mb-5 flex items-start gap-2.5 rounded-xl border border-[#7DB2FB]/40 bg-[#7DB2FB]/10 px-4 py-3 text-xs text-[#7DB2FB]">
              <Info className="w-4 h-4 mt-px shrink-0" strokeWidth={1.5}/>
              <span><strong className="font-semibold">Switch to landscape mode</strong> to see a live preview of your document as you type.</span>
            </div>
            <div className="mb-4">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] tracking-[0.24em] uppercase text-[#6B7280]">Step</span>
                <span data-testid="wizard-step-counter" className="font-mono text-xs text-[#A0AABF]">
                  <span className="text-[#CBA96C]">{step + 1}</span>/{steps.length}
                </span>
              </div>
              <Progress value={progress} data-testid="wizard-progress" className="h-1 bg-white/10 [&>div]:bg-[#CBA96C]"/>
            </div>
            {/* On the first step only: a document is named once, and repeating the box on every
                step would read as another question to answer. Optional by design — left blank,
                every list falls back to the document's type. */}
            {step === 0 && (
              <div className="mb-4 pb-4 border-b border-white/5">
                <Label className="text-[#A0AABF] text-xs uppercase tracking-[0.18em]">Name this document</Label>
                <Input
                  data-testid="wizard-doc-name" value={docName} maxLength={NAME_MAX}
                  onChange={e => setDocName(e.target.value)}
                  placeholder={`e.g. For ${shortType(service)} — ${firstName}`}
                  className="mt-1 bg-[#080C16] border-white/10 text-white placeholder:text-white/30 focus:border-[#CBA96C]"
                />
                <div className="flex items-center justify-between gap-3 mt-1">
                  {/* Says what happens if they skip it, rather than just "optional" — an unnamed
                      document still gets a distinct name (auto_document_name on the server), and
                      knowing that is what makes skipping a real choice instead of a gamble. */}
                  <div className="text-[10px] text-[#6B7280]">
                    Optional — leave blank and we'll call it <span className="text-[#A0AABF]">{shortType(service)}-{(txnId || "").slice(0, 8) || "xxxxxxxx"}</span>
                  </div>
                  <div className="text-[10px] text-[#6B7280] shrink-0">{docName.length}/{NAME_MAX}</div>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {steps[step]?.map(f => (
                <div key={f.key}>
                  <Label className="text-[#A0AABF] text-xs uppercase tracking-[0.18em]">{f.label}</Label>
                  {f.type === "list" ? (
                    <Select value={data[f.key] || ""} onValueChange={(v) => setField(f.key, v)}>
                      <SelectTrigger data-testid={`wizard-input-${f.key}`}
                        className="mt-1 bg-[#080C16] border-white/10 text-white focus:border-[#CBA96C]">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111623] border-white/10 text-white">
                        {(f.options || []).map(o => (
                          <SelectItem key={o} value={o} className="focus:bg-white/5 focus:text-white">{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : f.type === "text" && (f.max_length == null || f.max_length > 120) ? (
                    // long free text gets room to breathe; a short one stays a single line
                    <Textarea data-testid={`wizard-input-${f.key}`} value={data[f.key] || ""} onChange={upd(f.key)}
                      maxLength={f.max_length || undefined}
                      className="mt-1 bg-[#080C16] border-white/10 text-white placeholder:text-white/30 focus:border-[#CBA96C]"/>
                  ) : (
                    <Input data-testid={`wizard-input-${f.key}`}
                      type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"}
                      value={data[f.key] || ""} onChange={upd(f.key)}
                      maxLength={f.type === "text" && f.max_length ? f.max_length : undefined}
                      className="mt-1 bg-[#080C16] border-white/10 text-white placeholder:text-white/30 focus:border-[#CBA96C]"/>
                  )}
                  {f.max_length && f.type === "text" && (
                    <div className="text-[10px] text-[#6B7280] mt-1">{String(data[f.key] || "").length}/{f.max_length}</div>
                  )}
                  <FieldNotice fieldKey={f.key} value={data[f.key]} />
                </div>
              ))}
            </div>
            {isLast && (
              <div className="mt-4 bg-[#CBA96C]/10 border border-[#CBA96C]/30 rounded-lg p-3 text-xs text-[#DBC190]">
                Physical document will be delivered to your saved postal address after review. Please update your profile if not already added.
              </div>
            )}
          </div>
          </div>
          {/* Pinned: navigating the form is the one thing that must never require scrolling to
              reach, and it was the last element in a growing column. */}
          <div className="shrink-0 border-t border-white/5 bg-[#111623] px-6 md:px-8 py-4">
            <div className="max-w-md flex items-center justify-between gap-2">
              <Button variant="outline" disabled={step===0} onClick={()=>setStep(s=>s-1)} data-testid="wizard-back"
                className="bg-transparent border-white/20 text-white hover:bg-white/5 disabled:opacity-40"><ChevronLeft className="w-4 h-4 mr-1"/>Back</Button>
              <div className="flex gap-2">
                {/* Save Draft lives in the header bar now, next to the step counter */}
                {step < steps.length - 1 ? (
                  <Button onClick={()=>setStep(s=>s+1)} data-testid="wizard-next"
                    className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium">Next<ChevronRight className="w-4 h-4 ml-1"/></Button>
                ) : (
                  <Button disabled={busy || !allFilled} onClick={addToCart} data-testid="wizard-add-to-cart"
                    title={!allFilled ? "Fill in all fields before adding to cart" : undefined}
                    className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium disabled:opacity-40">{busy?"…":"Add to Cart"}</Button>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* RIGHT: PDF PREVIEW
            A fixed-height column that does not scroll: the sheet is scaled to fit the room left
            after the header row, so page one is whole and in view while the customer types. It
            used to be `overflow-y-auto` around a width-fitted sheet, which made an A4 page taller
            than the pane — so the preview opened part-way down its own scrollbar and the customer
            had to scroll a *preview* to see what they were previewing. Padding is tighter for the
            same reason: every pixel here is a pixel of document. */}
        <div className="hidden md:flex flex-col min-h-0 bg-[#080C16] px-5 py-3 relative">
          <div className="pointer-events-none absolute inset-0 opacity-30"
               style={{ background: "radial-gradient(600px 300px at 50% 20%, rgba(203,169,108,0.12), transparent 60%)" }} />
          <div className="relative flex-1 min-h-0 flex flex-col">
            {Preview}
          </div>
        </div>
      </div>
    </div>
  );
}

function UploadFlow({ serviceType }) {
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return toast.error("Please select a document");
    setBusy(true);
    try {
      const { data: t } = await api.post("/transactions", { service_type: serviceType, form_data: { notes }, price: serviceType==="property_verification"?4999:2499 });
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "document");
      await api.post(`/transactions/${t.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded — awaiting advocate review");
      nav(`/customer/txn/${t.id}`);
    } catch (e) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  const label = serviceType === "contract_review" ? "Contract Review" : "Property Legal Verification";
  return (
    <div className="min-h-[calc(100vh-3.5rem)] md:min-h-screen bg-[#080C16] text-[#F9F8F6]">
      <div className="backdrop-blur-xl bg-[#080C16]/80 border-b border-white/5">
        <div className="px-6 h-14 flex items-center text-sm">
          <Link to="/customer" data-testid="upload-back-link" className="inline-flex items-center gap-1 text-sm text-[#A0AABF] hover:text-white transition-colors"><ChevronLeft className="w-4 h-4" strokeWidth={1.5}/> Back</Link>
        </div>
      </div>
      <div className="max-w-2xl mx-auto p-10">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#CBA96C]">Upload Service</div>
        <h1 className="font-serif-display text-4xl mt-2">{label}</h1>
        <p className="mt-3 text-[#A0AABF]">Upload your document(s). An advocate will review and deliver a report to your dashboard.</p>
        <div className="mt-8 space-y-4 bg-[#111623] border border-white/10 rounded-2xl p-6">
          <div>
            <Label className="text-[#A0AABF] text-xs uppercase tracking-[0.18em]">Upload PDF / Scan</Label>
            <Input data-testid="upload-file-input" type="file" accept=".pdf,.jpg,.jpeg,.gif,.doc,.docx,.rtf,.txt,.zip"
              onChange={e=>setFile(e.target.files[0])}
              className="mt-2 bg-[#080C16] border-white/10 text-white file:text-[#CBA96C] file:bg-transparent file:border-0"/>
          </div>
          <div>
            <Label className="text-[#A0AABF] text-xs uppercase tracking-[0.18em]">Notes for the advocate</Label>
            <Textarea data-testid="upload-notes" value={notes} onChange={e=>setNotes(e.target.value)}
              className="mt-2 bg-[#080C16] border-white/10 text-white placeholder:text-white/30 focus:border-[#CBA96C]" rows={5}/>
          </div>
          <Button data-testid="upload-submit" disabled={busy} onClick={submit}
            className="w-full bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium h-11">
            <Upload className="w-4 h-4 mr-2"/>{busy?"Uploading…":"Submit for Review"}
          </Button>
        </div>
      </div>
    </div>
  );
}
